import React from 'react';
import { Progress } from '../../ui/progress';
import { ShieldCheck, Globe } from 'lucide-react';
import { useDevice } from '../DeviceContext';
import EmployeeDesktopFrame from './EmployeeDesktopFrame';

/**
 * Device-aware frame for the employee survey.
 *
 * Dispatches between a mobile/tablet single-column layout (this file) and a
 * richer desktop layout with a persistent step sidebar
 * ({@link EmployeeDesktopFrame}). Callers always just render
 * `<EmployeeFrame>`; the active preview device is read from
 * {@link useDevice}.
 *
 * Always lives inside a {@link DevicePreview}, so the outer bezel / browser
 * chrome is handled there — this frame just paints the inner app chrome.
 */
const MobileTabletFrame = ({ client, progress, step, totalSteps, wide, children }) => (
  <div className='w-full min-h-full flex flex-col bg-card'>
    <div className='px-5 pt-4 pb-3 border-b border-border flex items-center justify-between bg-card'>
      <div className='flex items-center gap-2'>
        <div className='w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-accent font-display text-sm font-bold'>
          {client[0]}
        </div>
        <div>
          <div className='text-[13px] font-semibold leading-tight'>{client}</div>
          <div className='text-[10px] text-muted-foreground uppercase tracking-[0.12em]'>WFH &amp; Commuting</div>
        </div>
      </div>
      <button
        type='button'
        className='flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted'
        title='Language'>
        <Globe size={12} strokeWidth={1.5} />
        EN
      </button>
    </div>

    {progress !== null && (
      <div className='px-5 pt-3 pb-2'>
        <div className='flex items-center justify-between mb-1.5'>
          <span className='text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground'>
            {step != null && totalSteps != null ? `Step ${step} of ${totalSteps}` : 'Progress'}
          </span>
          <span className='text-[11px] font-mono text-muted-foreground'>{progress}%</span>
        </div>
        <Progress value={progress} />
      </div>
    )}

    <div className={`flex-1 flex flex-col ${wide ? 'px-8 py-6' : 'px-5 py-5'}`}>
      <div className={wide ? 'max-w-[560px] w-full mx-auto flex flex-col flex-1' : 'flex flex-col flex-1'}>
        {children}
      </div>
    </div>

    <div className='px-5 py-2 border-t border-border bg-background/60 flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground'>
      <ShieldCheck size={10} strokeWidth={1.5} />
      Your responses are encrypted in transit and tied to your employer only.
    </div>
  </div>
);

const EmployeeFrame = ({
  client = 'Unlimited Group',
  progress = null,
  step = null,
  totalSteps = null,
  currentSlug = null,
  children,
}) => {
  const { device } = useDevice();

  if (device === 'desktop') {
    return (
      <EmployeeDesktopFrame
        client={client}
        progress={progress}
        step={step}
        totalSteps={totalSteps}
        currentSlug={currentSlug}>
        {children}
      </EmployeeDesktopFrame>
    );
  }

  return (
    <MobileTabletFrame
      client={client}
      progress={progress}
      step={step}
      totalSteps={totalSteps}
      wide={device === 'tablet'}>
      {children}
    </MobileTabletFrame>
  );
};

export default EmployeeFrame;
